import { message } from "antd";
import { getFolderPath } from "../../utils/navigationUrl";
import { checkIsFileDeleted } from "../../slice/HomeSlice";
import { postHighlightedMatter } from "../../slice/MatterDetailsSlice";
import { MS_FILE_TYPES, PDF_FILE_TYPES } from "../../utils/constants";

/**
 * Build row click handlers for Home-page tables that open a document in
 * the matter workspace when the row is clicked.
 *
 * @param {Function} navigate     react-router navigate
 * @param {Function} dispatch     redux dispatch
 * @param {Function} handleError  user-facing error helper
 * @param {Object}   [options]
 * @param {Object}   [options.navState]
 *   Optional state payload to attach to the matter-page navigate() call.
 *   This is how callers signal cross-page intent (e.g. the My Reviews /
 *   Shared for Review tabs pass `{ autoOpenReviewNotes: true }` so the
 *   matter workspace can pop the chronological comment thread on arrival
 *   without the user hunting for the "Review Notes" item in the More menu).
 *   When omitted, navigate() is called with a single positional arg —
 *   keeps existing call sites and tests unchanged.
 * @returns row props for an antd Table.
 */
export const getRowProps = (navigate, dispatch, handleError, options = {}) => (record) => ({
  onClick: async (e) => {
    e.stopPropagation();
    const { file_type, relative_path } = record?.document || {};

    if (record?.document?.id) {
      const response = await dispatch(checkIsFileDeleted(record.document.id));
      if (response?.payload?.data?.deleted) {
        handleError(
          "This file is no longer available. It may have been deleted or removed during the latest sync."
        );
      } else {
        // Always store the folder path
        const { folderPath, matterId, documentId } =
          getFolderPath(relative_path);

        if (
          PDF_FILE_TYPES.includes(file_type) ||
          MS_FILE_TYPES.includes(file_type)
        ) {
          localStorage.setItem("relativePath", folderPath);
          // Build payload with tab_action for documents
          const userId = Number(localStorage.getItem("user_id"));
          const highlightPayload = {
            matterId,
            docId: documentId,
            docType: "document",
            tabAction: {
              usr_id: userId,
              document_id: String(documentId),
              matter_id: String(matterId),
              action: "open"
            }
          };
          dispatch(
            postHighlightedMatter(highlightPayload)
          ).then(() => {
            const target = `/matter${relative_path}${
              options?.aiTab ? `?ai=${encodeURIComponent(options.aiTab)}` : ""
            }`;
            if (options?.navState) {
              navigate(target, { state: options.navState });
            } else {
              navigate(target);
            }
          });
        }
      }
    }
  },
});

/**
 * Build the click handler for Home's Messages table.
 *
 * Deliberately does NOT reuse getRowProps: that handler only opens a row
 * when the file is a previewable PDF/Office type and otherwise does
 * nothing at all — correct for tables like My Reviews, where an
 * unsupported type genuinely has nothing to open. A message conversation
 * exists independent of whether its document is inline-previewable, so
 * clicking a message thread must always land on the conversation; the
 * document pane falls back to its own unsupported-type handling when it
 * can't preview the file.
 *
 * Uses antd's global `message` for the deleted-file case (rather than a
 * `handleError` callback tied to Home's own local toast state) because
 * this function always navigates away right after - a toast owned by
 * the page being left would unmount before the user could see it.
 *
 * @returns {Function} async (thread) => void
 */
export const getMessageThreadOpener = (navigate, dispatch) => async (thread) => {
  const navState = {
    autoOpenMessages: true,
    messageThreadId: thread?.id,
  };
  const documentId = thread?.document?.id;
  const documentPath = thread?.document?.relative_path;
  // A message can be associated with a document that has not been placed in
  // the matter file tree yet. Its conversation is still available at the
  // matter level, but it has no document URL to open.
  if (!documentId || !documentPath) {
    navigate(`/matter/${thread.matter_id}?ai=messages`, { state: navState });
    return;
  }

  const response = await dispatch(checkIsFileDeleted(documentId));
  if (response?.payload?.data?.deleted) {
    // handleError renders Home's own local ErrorToast, which unmounts
    // (and takes the toast with it) the instant navigate() below leaves
    // this page. Use antd's global message instead - it survives the
    // route change so the user actually sees why they landed on the
    // matter view instead of the specific document.
    message.error(
      "This file is no longer available. It may have been deleted or removed during the latest sync."
    );
    navigate(`/matter/${thread.matter_id}?ai=messages`, { state: navState });
    return;
  }

  const { folderPath, matterId, documentId: docId } = getFolderPath(
    documentPath
  );
  localStorage.setItem("relativePath", folderPath);
  const userId = Number(localStorage.getItem("user_id"));
  const openConversation = () =>
    navigate(`/matter${documentPath}?ai=messages`, {
      state: navState,
    });
  return dispatch(
    postHighlightedMatter({
      matterId,
      docId,
      docType: "document",
      tabAction: {
        usr_id: userId,
        document_id: String(docId),
        matter_id: String(matterId),
        action: "open",
      },
    })
  ).then(openConversation, openConversation);
};

export function handleErrorFactory(setIsLoading, setError, setShowNotification) {
  return function handleError(message) {
    setIsLoading(false);
    const id = Date.now().toString();
    setError([{ id, newMsg: message }]);
    setShowNotification(true);
  };
}

export const handleAPICalls = (dispatch, setIsLoading, {
  fetchMyReviewsList,
  fetchSharedReviewsList,
  fetchMySignatureList,
  fetchSentForSignatureList,
  fetchVeteransAffairsList,
  fetchMessagesList,
  fetchTasksList
}) => async (tab) => {
  setIsLoading(true);
  try {
    if (tab === "sharedReview") {
      await dispatch(fetchSharedReviewsList()).unwrap();
    } else if (tab === "myReview") {
      await dispatch(fetchMyReviewsList()).unwrap();
    } else if (tab === "needsMySignature") {
      await dispatch(fetchMySignatureList()).unwrap();
    } else if (tab === "sentForSignature") {
      await dispatch(fetchSentForSignatureList()).unwrap();
    } else if (tab === "veteransAffairs") {
      await dispatch(fetchVeteransAffairsList()).unwrap();
    } else if (tab === "tasks") {
      await dispatch(fetchTasksList()).unwrap();
    } else if (tab === "messages") {
      await dispatch(fetchMessagesList()).unwrap();
    }
  } catch (error) {
    console.error(`Error in handleAPICalls for tab ${tab}:`, error);
    // Re-throw the error so it can be handled by the calling function
    throw error;
  } finally {
    setIsLoading(false);
  }
};

export const initializeData = async (handleAPICalls, handleError) => {
  try {
    await handleAPICalls("myReview");
  } catch (error) {
    console.error("Error initializing data:", error);
    if (error.status >= 500) {
      handleError("Server error. Please try again later.");
    } else {
      handleError(error.message || "Failed to initialize data");
    }
  }
}; 